import type { MaturityOption } from '@moment/horizon-data-access/proposal'
import type { FilterBuilderResponseUnion } from '@moment/trading-types/filter'
import type { FilterObject } from '@moment/trading-types/filter-expression'
import type {
  BondType,
  CouponType,
  TreasurySubtype,
} from '@moment/trading-types/reference-data'
import type { DiversificationConstraintValue } from '@/components/molecules/diversification-constraint/types'
import type { TargetBandState } from '@/components/molecules/target-band'
import type { FilterListShort } from '@/legacy-api/filter-lists-slice.gen'
import type { ModelDetailResponse, ModelPreview } from '@/types/api/models'
import type { RatingMode } from '@/types/enums/models'

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import {
  type BucketType,
  DEFAULT_MODEL_BUILDER_VALUES,
} from '@/components/pages/model-builder/default-values'
import { transformModelDetailToBuilder } from '@/components/pages/model-builder/helpers'

enum ModelBuilderTabs {
  ModelSetup = 'Model setup',
  Universe = 'Universe',
  TargetAverages = 'Target averages',
  Allocation = 'Allocation',
  Diversification = 'Diversification',
}

export enum ModelBuilderSidePane {
  Preview = 'preview',
}

// Type for filter list objects with only the fields we need
type FilterListData = Pick<FilterListShort, 'list_id' | 'name' | 'field'>

interface ModelBuilderConfig {
  activeTab: ModelBuilderTabs
}

// Add metadata tracking to the state
interface FormFieldMetadata {
  touched: boolean
  dirty: boolean
  // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
  initialValue: any
}

type FormMetadata = {
  [K in keyof ModelBuilder]: FormFieldMetadata
}

// Initialize metadata for all fields
function createFormMetadata(
  initialValues: ModelBuilder,
  existing: boolean = false,
): FormMetadata {
  return Object.keys(initialValues).reduce((acc, key) => {
    acc[key as keyof ModelBuilder] = {
      touched: existing,
      dirty: existing,
      initialValue: initialValues[key as keyof ModelBuilder],
    }
    return acc
  }, {} as FormMetadata)
}

export interface AllocationConstraintState {
  id?: string
  targetBand: TargetBandState | null
  conditions: FilterBuilderResponseUnion[]
}

export interface ModelBuilder {
  id: string | null
  name: string
  description: string
  cash: number | null
  assetClasses: BondType[]
  sharing_organization_id: string
  municipalState: string | null
  municipalStatePreference: string | null

  // target averages
  spRatingTarget: string | null
  spRatingMin: string | null
  spRatingMax: string | null
  spRatingBuffer: number | null
  ytwTarget: number | null
  ytwMin: number | null
  ytwMax: number | null
  ytwBuffer: number | null
  couponTarget: number | null
  couponMin: number | null
  couponMax: number | null
  couponBuffer: number | null

  // bucket stats
  bucketType: BucketType
  bucketWidth: number
  rangeInPeriods: number[] | null
  bucketWeightMin: number | null
  bucketWeightMax: number | null
  bucketWeightBuffer: number | null
  /** Dollar rung tolerance from a loaded ladder; used only while bucketWeightBuffer is null. */
  ladderMarketValueTolerance: number | null
  useCallAdjustedMaturities: boolean
  maturityOption: MaturityOption | null
  ladderPolicyEnabled: boolean

  // universe constraints
  couponRange: number[] | null
  ytwRange: number[] | null // close_ytw
  priceRange: number[] | null // close_price
  maturityRange: number[] | null // maturity_in_years
  nextCallRange: number[] | null // next_call_in_years
  workoutRange: number[] | null // workout_in_years
  durationRange: number[] | null
  liquidityScoreRange: number[] | null
  minSpRange: string | null
  maxSpRange: string | null
  spRatingMode: RatingMode
  minLowestRatingRange: string | null
  maxLowestRatingRange: string | null
  lowestRatingMode: RatingMode
  minMoodysRange: string | null
  maxMoodysRange: string | null
  moodysRatingMode: RatingMode
  issuesSizeRange: number[] | null
  couponType: CouponType[] | null
  treasurySubtype: TreasurySubtype[] | null
  excludeSectors: string[] | null
  excludeIssuers: string[] | null
  excludeMunicipalStates: string[] | null
  includeTickers: string[] | null
  excludeTickers: string[] | null
  includeInstruments: string[] | null
  excludeUseOfProceeds: string[] | null
  municipalTaxableFederal: [string]
  privatePlacement: [string]
  regulationS: [string]
  amtApplicable: [string]
  belowDeMinimisThreshold: [string]
  includeCurrencies: string[] | null
  includeCountryDomicile: string[] | null
  excludeGicsSectorL4: string[] | null
  excludeIceSectorL4: string[] | null
  includeFilterList: { list: FilterListData; scope?: FilterObject } | null
  excludeFilterList: { list: FilterListData; scope?: FilterObject } | null
  fdicInsured: [string]
  vrdoIndicator: [string]

  excludeStructures: ('perpetual' | 'sinking' | 'callable' | 'bullet')[]
  perpetual: boolean
  sinkingFund: boolean
  callable: boolean

  // allocation constraints (model setup tab)
  stateMinWeight: number | null
  stateTargetWeight: number | null
  stateMaxWeight: number | null
  stateBuffer: number | null
  showMinMax: boolean

  // diversification constraints
  weightPerIssuer: DiversificationConstraintValue | null
  weightPerSector: DiversificationConstraintValue | null
  weightPerState: DiversificationConstraintValue | null
  weightPerBond: DiversificationConstraintValue | null
  weightPerMaturityYear: DiversificationConstraintValue | null
  weightPerFdicCode: DiversificationConstraintValue | null
  weightPerInsurer: DiversificationConstraintValue | null
  weightPerUltimateBorrower: DiversificationConstraintValue | null
  weightPerUltimateParent: DiversificationConstraintValue | null

  // target averages
  spRatingTargetBand: TargetBandState | null
  lowestRatingTargetBand: TargetBandState | null
  moodysRatingTargetBand: TargetBandState | null
  couponTargetBand: TargetBandState | null
  durationTargetBand: TargetBandState | null
  weightedTargetBand: TargetBandState | null
  liquidityScoreTargetBand: TargetBandState | null
  convexityTargetBand: TargetBandState | null
  maturityTargetBand: TargetBandState | null

  // allocation constraints (allocation tab)
  allocationConstraints: AllocationConstraintState[] | null

  // constraint ID map for round-trip preservation (keyed by constraint type + field)
  constraintIdMap?: Record<string, string>
}

// Allocation constraint fields
export const ALLOCATION_CONSTRAINT_FIELDS: (keyof ModelBuilder)[] = [
  'stateMinWeight',
  'stateTargetWeight',
  'stateMaxWeight',
  'stateBuffer',
  'showMinMax',
  'allocationConstraints',
]

// Diversification constraint fields
export const DIVERSIFICATION_CONSTRAINT_FIELDS: (keyof ModelBuilder)[] = [
  'weightPerIssuer',
  'weightPerSector',
  'weightPerState',
  'weightPerBond',
  'weightPerMaturityYear',
  'weightPerFdicCode',
  'weightPerInsurer',
  'weightPerUltimateBorrower',
  'weightPerUltimateParent',
]

// Target average constraint fields
export const TARGET_AVERAGE_CONSTRAINT_FIELDS: (keyof ModelBuilder)[] = [
  'spRatingTargetBand',
  'moodysRatingTargetBand',
  'couponTargetBand',
  'durationTargetBand',
  'weightedTargetBand',
  'liquidityScoreTargetBand',
  'convexityTargetBand',
  'maturityTargetBand',
]

export interface ModelBuilderState {
  config: ModelBuilderConfig
  configVersion: string
  activePane: ModelBuilderSidePane | null
  model: ModelBuilder
  modelPreviewData: ModelPreview | null
  formMetadata: FormMetadata
  isPreviewLoading: boolean
  template: string | null
  existingModelHydrated: boolean
}

const initialState: ModelBuilderState = {
  config: {
    activeTab: ModelBuilderTabs.ModelSetup,
  },
  configVersion: '1.0',
  activePane: null,
  model: DEFAULT_MODEL_BUILDER_VALUES,
  modelPreviewData: null,
  formMetadata: createFormMetadata(DEFAULT_MODEL_BUILDER_VALUES),
  isPreviewLoading: false,
  template: null,
  existingModelHydrated: false,
}

const modelBuilderSlice = createSlice({
  name: 'modelBuilder',
  initialState,
  reducerPath: 'modelBuilder',
  reducers: {
    initializeModelBuilderConfig: (
      state,
      action: PayloadAction<{ config: ModelBuilderConfig; version: string }>,
    ) => {
      state.config = action.payload.config
      state.configVersion = action.payload.version
    },
    resetModelBuilderConfig: (state) => {
      state.config = initialState.config
      state.configVersion = initialState.configVersion
      state.activePane = initialState.activePane
      state.model = initialState.model
      state.modelPreviewData = initialState.modelPreviewData
      state.formMetadata = createFormMetadata(initialState.model)
      state.template = initialState.template
      state.existingModelHydrated = initialState.existingModelHydrated
    },
    setModelBuilderConfig: (
      state,
      action: PayloadAction<ModelBuilderConfig>,
    ) => {
      state.config = action.payload
    },
    setActiveTab: (state, action: PayloadAction<ModelBuilderTabs>) => {
      state.config.activeTab = action.payload
    },
    setActivePane: (state, action) => {
      state.activePane = action.payload
    },
    setModelField: <T extends keyof ModelBuilder>(
      state: ModelBuilderState,
      action: PayloadAction<{ field: T; value: ModelBuilder[T] }>,
    ) => {
      const { field, value } = action.payload

      state.model[field] = value

      // Update metadata
      state.formMetadata[field] = {
        ...state.formMetadata[field],
        touched: true,
        dirty: value !== DEFAULT_MODEL_BUILDER_VALUES[field],
      }
    },
    setModel: (state, action: PayloadAction<ModelBuilder>) => {
      state.model = action.payload
      // Update all metadata to reflect the restored model
      state.formMetadata = createFormMetadata(action.payload, true)
    },
    setPreviewData: (state, action) => {
      state.modelPreviewData = action.payload
    },
    touchField: <T extends keyof ModelBuilder>(
      state: ModelBuilderState,
      action: PayloadAction<T>,
    ) => {
      // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
      state.formMetadata[action.payload]!.touched = true
    },

    resetField: <T extends keyof ModelBuilder>(
      state: ModelBuilderState,
      action: PayloadAction<T>,
    ) => {
      const field = action.payload
      // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
      state.model[field] = state.formMetadata[field]!.initialValue
      state.formMetadata[field] = {
        touched: false,
        dirty: false,
        // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
        initialValue: state.formMetadata[field]!.initialValue,
      }
    },

    resetFormMetadata: (state) => {
      state.formMetadata = createFormMetadata(state.model)
    },

    setPreviewLoading: (state, action) => {
      state.isPreviewLoading = action.payload
    },

    setFromExistingModel: (
      state,
      {
        payload,
      }: PayloadAction<{
        data: ModelDetailResponse | undefined
        template: string | undefined
      }>,
    ) => {
      if (!payload?.data) return
      const { data, template } = payload
      const model = transformModelDetailToBuilder(data, template)

      // Don't overwrite persisted state if same model is already loaded
      if (state.model.id === model.id && state.existingModelHydrated) {
        return
      }

      // Check if ladder policy fields are all null/empty and set ladderPolicyEnabled accordingly
      const ladderPolicyFieldsAllNull =
        model.rangeInPeriods === null && model.bucketWeightBuffer === null

      if (ladderPolicyFieldsAllNull) {
        model.ladderPolicyEnabled = false
      }

      const formMetadata = {
        ...createFormMetadata(DEFAULT_MODEL_BUILDER_VALUES),
        ...createFormMetadata(model, true),
      }

      state.model = model
      state.formMetadata = formMetadata
      state.existingModelHydrated = true
    },
    setTemplate: (state, action: PayloadAction<string>) => {
      state.template = action.payload
    },
  },
})

// Helper functions to use in components
export const getFieldMetadata = (
  state: ModelBuilderState,
  field: keyof ModelBuilder,
  // biome-ignore lint/style/noNonNullAssertion: Bulk suppression as part of biome migration
): FormFieldMetadata => state.formMetadata[field]!

export const {
  initializeModelBuilderConfig,
  setModelField,
  // Helper functions
  touchField,
} = modelBuilderSlice.actions

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default modelBuilderSlice
